import React, { FC, useState, useRef, useEffect } from 'react';
import {
  EmployeeAverageAgeRequestCode,
  HolidayAndWayOfWorkingRequestCode,
  LanguageSkillsRequestCode,
  PositionsRequestCode,
  WelfareBenefitsRequestCode,
  WorkplaceEnvironmentRequestCode,
  RequirementsRequestCode,
  JobListResponse,
} from '@doda/api-client/aspida/@types';
import { makeStaticUrl } from '@doda/common/src/logics/staticUrl';
import { useSelector } from 'react-redux';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { DODA_SITE_URL } from '@doda/common/src/constants/urls';
import { generateOpValues } from '@doda/common/src/logics/JobContent/JobTrend/generateOpValues';
import { KODAWARI } from '@doda/common/src/constants/seo/kodawari';
import {
  LABEL_FLAG,
  UPPER_AND_LOWER_HA,
} from '@doda/common/src/constants/seo/jobTrendConstants';
import { createPreBtn } from '@doda/common/src/logics/JobContent/createPreBtn';
import { createOpParams } from '@doda/common/src/logics/JobContent/apiRequest/createOpParams';
import {
  createJobKodawariCount,
  createJobKodawariCountLanguage,
  createOpAndUrl,
} from '@doda/common/src/logics/JobContent/JobTrend/createJobKodawariCount';
import { jobTrendRankingSelector } from '@doda/common/src/redux/features/JobContent/JobTrend/jobTrendRanking';
import { Slider } from 'components/RecommendSlider/components/Slider';
import { useAppDispatch } from '@doda/common/src/redux/hooks';
import { measureBookmarkRecommendCard } from 'pages/DodaFront/View/MatchJobList/logics/analytics';
import { OP_SEARCH_ITEMS } from '@doda/common/src/constants/searchItems';
import {
  measureRecommendVisible,
  measureClickRecommendCard,
} from '@doda/common/src/logics/JobContent/JobTrend/analytics';
import { useIntersection } from '@doda/common/src/hooks/useIntersection';
import { convertQuery } from '@doda/common/src/logics/JobContent/analytics';
import { JobRecommendCard } from '../JobRecommendCard';
import styles from './index.module.scss';

/**
 * InitialJobRecommenndのProps
 * 初期表示用求人レコメンドコンポーネント
 * @param targetName ページに訪れたパラメータの項目名
 * @param jobList 初期表示用に取得した求人一覧
 * @param flag どのカテゴリかを判定するためのフラグ
 * @param initialUrl 初期表示される求人一覧へのURL
 * @param analyticsSelectedValue 計測用
 * @param selectedCount 選択された棒グラフの求人数
 */
interface Props {
  targetName: string;
  jobList: JobListResponse;
  flag: string;
  initialUrl: string;
  analyticsSelectedValue: string;
  selectedCount: number;
}

export const InitialJobRecommend: FC<Props> = ({
  targetName,
  jobList,
  flag,
  initialUrl,
  analyticsSelectedValue,
  selectedCount,
}) => {
  const queryParams = useSelector(appQueryParamsSelector);
  const rankingList = useSelector(jobTrendRankingSelector);
  // カテゴリごとのidを取得し、opに変換してループさせる
  // 募集採用情報_ランキングをもとに出力しないものを除外する
  const {
    jobCountValue: jobCountValueRequirements,
    kodawariIdList: requirementsList,
  } = createJobKodawariCount(
    KODAWARI.requirements,
    rankingList.requirements[0],
    rankingList.requirements[1]
  );
  const requirementsOp = generateOpValues({
    requirementsList: requirementsList as RequirementsRequestCode[],
  });

  // 会社・職場の雰囲気_ランキングをもとに出力しないものを除外する
  const {
    jobCountValue: jobCountValueWorkplace,
    kodawariIdList: workplaceEnvironmentList,
  } = createJobKodawariCount(
    KODAWARI.workplaceEnvironment,
    rankingList.workplaceEnvironment[0],
    rankingList.workplaceEnvironment[1]
  );
  const workplaceEnvironmentOp = generateOpValues({
    workplaceEnvironmentList:
      workplaceEnvironmentList as WorkplaceEnvironmentRequestCode[],
  });

  // 休日・働き方_ランキングをもとに出力しないものを除外する
  const {
    jobCountValue: jobCountValueHoliday,
    kodawariIdList: holidayAndWayOfWorkingList,
  } = createJobKodawariCount(
    KODAWARI.holidayAndWayOfWorking,
    rankingList.holidayAndWayOfWorking[0],
    rankingList.holidayAndWayOfWorking[1]
  );
  const holidayAndWayOfWorkingOp = generateOpValues({
    holidayAndWayOfWorkingList:
      holidayAndWayOfWorkingList as HolidayAndWayOfWorkingRequestCode[],
  });

  // 平均年齢_ランキングをもとに出力しないものを除外する
  const { kodawariIdList: employeeAverageAgeList } = createJobKodawariCount(
    KODAWARI.employeeAverageAge,
    rankingList.employeeAverageAge[0],
    rankingList.employeeAverageAge[1]
  );
  const employeeAverageAgeOp = generateOpValues({
    employeeAverageAgeList:
      employeeAverageAgeList as EmployeeAverageAgeRequestCode[],
  });

  // 雇用形態
  const employmentStatusOp = ['17', '18', ['19', '57', '58', '59', '64']];

  // 福利厚生_ランキングをもとに出力しないものを除外する
  const {
    jobCountValue: jobCountValueWelfareBenefits,
    kodawariIdList: welfareBenefitsList,
  } = createJobKodawariCount(
    KODAWARI.welfareBenefits,
    rankingList.welfareBenefits[0],
    rankingList.welfareBenefits[1]
  );
  const welfareBenefitsOp = generateOpValues({
    welfareBenefitsList: welfareBenefitsList as WelfareBenefitsRequestCode[],
  });

  // 仕事内容・語学_ランキングをもとに出力しないものを除外する
  const {
    jobCountValueLanguageSkillsAndPositions,
    jobCountValueLanguageSkills,
    jobCountValuePositions,
  } = createJobKodawariCountLanguage(
    rankingList.positionsAndLanguageSkills[0],
    rankingList.positionsAndLanguageSkills[1]
  );

  const languageSkillsList: string[] = [];
  jobCountValueLanguageSkills.forEach((kodawari) => {
    return languageSkillsList.push(kodawari.value);
  });
  const languageSkillsOp = generateOpValues({
    languageSkillsList: languageSkillsList as LanguageSkillsRequestCode[],
  });

  const positionsList: string[] = [];
  jobCountValuePositions.forEach((kodawari) => {
    return positionsList.push(kodawari.value);
  });
  const positionsOp = generateOpValues({
    positionsList: positionsList as PositionsRequestCode[],
  });

  const { languageAndPositionsUrl, languageAndPositionsOP } = createOpAndUrl(
    queryParams,
    languageSkillsOp,
    positionsOp
  );

  const dispatch = useAppDispatch();
  const handleBookmark = (recommend) => {
    dispatch(measureBookmarkRecommendCard(recommend.itemNo));
  };

  // こだわり一覧
  const opList = Object.keys(OP_SEARCH_ITEMS).map(
    (key) => OP_SEARCH_ITEMS[key]
  );

  // 計測用パラメータ値
  const analyticsValue = `${convertQuery(queryParams)}_${analyticsSelectedValue}`;

  // レコメンドvisible時計測用
  const ref = useRef<HTMLDivElement>(null);
  const { intersecting } = useIntersection(ref);
  const [visibled, setVisibled] = useState(false);
  useEffect(() => {
    if (intersecting && !visibled) {
      measureRecommendVisible(
        analyticsValue,
        jobList.results ? jobList.results : null
      );
      setVisibled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intersecting]);

  // レコメンドカードクリック時計測用
  const recommendType = `${analyticsValue}`;
  const effectName = `${recommendType}_pc`;
  const handleClickCard = () => {
    dispatch(
      measureClickRecommendCard({
        recommendType,
        effectName,
      })
    );
  };

  // レコメンドロード時計測用class
  const recommendIdxClass = `recommendIdxranking_${convertQuery(queryParams)}`;
  return (
    <div className={styles.initialDisplay__jobRecommend} ref={ref}>
      <input
        name="recommendIdx"
        id="recommendIdx"
        className={recommendIdxClass}
        type="hidden"
        value="0"
      />
      {selectedCount !== 0 ? (
        <Slider>
          {jobList.results?.map((list, index) => {
            return (
              <JobRecommendCard
                key={index}
                companyName={list.corporateName}
                jobName={list.occupationName}
                area={list.jobState}
                income={list.salary}
                jobDetailUrl={`${list.url}?recommendID=ranking_${recommendType}`}
                isNew={false}
                isDeadline={false}
                onClickFavorite={() => {
                  if (handleBookmark) handleBookmark(list);
                }}
                onClickSaved={undefined}
                onClickCardDetail={handleClickCard}
                bookmarkListUrl="/dcfront/bookmark/bookmarkList/"
                jid={list.jid}
                saveBookmarkRequestBody={{ device: 'PC' }}
                isBookmarked={list.isBookmarkSaved}
              />
            );
          })}
        </Slider>
      ) : (
        <div className={styles.initialDisplay__nothingJobs}>
          該当する求人はありません
        </div>
      )}

      <div className={styles.initialDisplay__seoLink}>
        {(() => {
          if (flag === LABEL_FLAG.REQUIREMENT) {
            return requirementsOp.map((op, index) => {
              const appQueryParamsRequire = createPreBtn(queryParams);
              if (appQueryParamsRequire.op) {
                if (appQueryParamsRequire.op === op) {
                  return <></>;
                }
                appQueryParamsRequire.op = [appQueryParamsRequire.op, op].sort(
                  (a, b) => a - b
                );
              } else {
                appQueryParamsRequire.op = op;
              }
              const url = makeStaticUrl(
                appQueryParamsRequire,
                DODA_SITE_URL.jobSearchList
              ).replace(/,/g, '%2C');
              if (jobCountValueRequirements[index].count === 0) {
                return <></>;
              }

              const kodawariName = opList.find((kodawari) => {
                return kodawari.id === op;
              });
              if (initialUrl !== url) {
                return (
                  <a
                    className="initialDisplay__seoLink--hidden"
                    key={index}
                    href={url}
                    rel={queryParams.ne || queryParams.es ? 'nofollow' : ''}
                  >
                    {targetName}×{kodawariName?.name} すべての求人を見る
                  </a>
                );
              }
              return <></>;
            });
          }
          if (flag === LABEL_FLAG.WORKPLACE_ENVIRONMENT) {
            return workplaceEnvironmentOp.map((op, index) => {
              const appQueryParamsWorkplace = createPreBtn(queryParams);
              if (appQueryParamsWorkplace.op) {
                if (appQueryParamsWorkplace.op === op) {
                  return <></>;
                }
                appQueryParamsWorkplace.op = [
                  appQueryParamsWorkplace.op,
                  op,
                ].sort((a, b) => a - b);
              } else {
                appQueryParamsWorkplace.op = op;
              }
              const url = makeStaticUrl(
                appQueryParamsWorkplace,
                DODA_SITE_URL.jobSearchList
              ).replace(/,/g, '%2C');
              if (jobCountValueWorkplace[index].count === 0) {
                return <></>;
              }
              const kodawariName = opList.find((kodawari) => {
                return kodawari.id === op;
              });

              if (initialUrl !== url) {
                return (
                  <a
                    className="initialDisplay__seoLink--hidden"
                    key={index}
                    href={url}
                    rel={queryParams.ne || queryParams.es ? 'nofollow' : ''}
                  >
                    {targetName}×{kodawariName?.name} すべての求人を見る
                  </a>
                );
              }
              return <></>;
            });
          }
          if (flag === LABEL_FLAG.HOLIDAY_AND_WAY_OF_WORKING) {
            return holidayAndWayOfWorkingOp.map((op, index) => {
              const appQueryParamsholidayAndWayOfWorking =
                createPreBtn(queryParams);
              if (appQueryParamsholidayAndWayOfWorking.op) {
                if (appQueryParamsholidayAndWayOfWorking.op === op) {
                  return <></>;
                }
                appQueryParamsholidayAndWayOfWorking.op = [
                  appQueryParamsholidayAndWayOfWorking.op,
                  op,
                ].sort((a, b) => a - b);
              } else {
                appQueryParamsholidayAndWayOfWorking.op = op;
              }
              const url = makeStaticUrl(
                appQueryParamsholidayAndWayOfWorking,
                DODA_SITE_URL.jobSearchList
              ).replace(/,/g, '%2C');
              if (jobCountValueHoliday[index].count === 0) {
                return <></>;
              }
              const kodawariName = opList.find((kodawari) => {
                return kodawari.id === op;
              });

              if (initialUrl !== url) {
                return (
                  <a
                    className="initialDisplay__seoLink--hidden"
                    key={index}
                    href={url}
                    rel={queryParams.ne || queryParams.es ? 'nofollow' : ''}
                  >
                    {targetName}×{kodawariName?.name} すべての求人を見る
                  </a>
                );
              }
              return <></>;
            });
          }
          if (flag === LABEL_FLAG.EMPLOYEE_AVERAGE_AGE) {
            return employeeAverageAgeOp.map((op, index) => {
              const appQueryParamsemployeeAverageAge =
                createPreBtn(queryParams);
              if (appQueryParamsemployeeAverageAge.op) {
                if (appQueryParamsemployeeAverageAge.op === op) {
                  return <></>;
                }
                const hydrogenOp = appQueryParamsemployeeAverageAge.op
                  ? createOpParams(appQueryParamsemployeeAverageAge.op)
                  : undefined;
                if (!hydrogenOp?.employeeAverageAge) {
                  appQueryParamsemployeeAverageAge.op = [
                    appQueryParamsemployeeAverageAge.op,
                    op,
                  ].sort((a, b) => a - b);
                } else {
                  appQueryParamsemployeeAverageAge.op = op;
                }
              } else {
                appQueryParamsemployeeAverageAge.op = op;
              }
              const url = makeStaticUrl(
                appQueryParamsemployeeAverageAge,
                DODA_SITE_URL.jobSearchList
              ).replace(/,/g, '%2C');
              if (rankingList.employeeAverageAge[0][index] === 0) {
                return <></>;
              }
              const kodawariName = opList.find((kodawari) => {
                return kodawari.id === op;
              });

              if (initialUrl !== url) {
                return (
                  <a
                    className="initialDisplay__seoLink--hidden"
                    key={index}
                    href={url}
                    rel={queryParams.ne || queryParams.es ? 'nofollow' : ''}
                  >
                    {targetName}×{kodawariName?.name} すべての求人を見る
                  </a>
                );
              }
              return <></>;
            });
          }
          if (flag === LABEL_FLAG.EMPLOYMMENT_STATUS) {
            return employmentStatusOp.map((op, index) => {
              const appQueryParamsemploymentStatus = createPreBtn(queryParams);
              if (appQueryParamsemploymentStatus.op) {
                if (appQueryParamsemploymentStatus.op === op) {
                  return <></>;
                }
                const hydrogenOp = appQueryParamsemploymentStatus.op
                  ? createOpParams(appQueryParamsemploymentStatus.op)
                  : undefined;
                if (!hydrogenOp?.employmentStatus) {
                  appQueryParamsemploymentStatus.op = [
                    appQueryParamsemploymentStatus.op,
                    op,
                  ].sort((a, b) => a - b);
                } else {
                  appQueryParamsemploymentStatus.op = op;
                }
              } else {
                appQueryParamsemploymentStatus.op = op;
              }
              const url = makeStaticUrl(
                appQueryParamsemploymentStatus,
                DODA_SITE_URL.jobSearchList
              ).replace(/,/g, '%2C');
              if (rankingList.employmentStatus[0][index] === 0) {
                return <></>;
              }
              const kodawariName = opList.find((kodawari) => {
                if (op.length > 2) {
                  return kodawari.id === op[0];
                }
                return kodawari.id === op;
              });

              if (initialUrl !== url) {
                return (
                  <a
                    className="initialDisplay__seoLink--hidden"
                    key={index}
                    href={url}
                    rel={queryParams.ne || queryParams.es ? 'nofollow' : ''}
                  >
                    {targetName}×{kodawariName?.name} すべての求人を見る
                  </a>
                );
              }
              return <></>;
            });
          }
          if (flag === LABEL_FLAG.WELFARE_BENEFITS) {
            return welfareBenefitsOp.map((op, index) => {
              const appQueryParamsewelfareBenefits = createPreBtn(queryParams);
              if (appQueryParamsewelfareBenefits.op) {
                if (appQueryParamsewelfareBenefits.op === op) {
                  return <></>;
                }
                appQueryParamsewelfareBenefits.op = [
                  appQueryParamsewelfareBenefits.op,
                  op,
                ].sort((a, b) => a - b);
              } else {
                appQueryParamsewelfareBenefits.op = op;
              }
              const url = makeStaticUrl(
                appQueryParamsewelfareBenefits,
                DODA_SITE_URL.jobSearchList
              ).replace(/,/g, '%2C');
              if (jobCountValueWelfareBenefits[index].count === 0) {
                return <></>;
              }
              const kodawariName = opList.find((kodawari) => {
                return kodawari.id === op;
              });

              if (initialUrl !== url) {
                return (
                  <a
                    className="initialDisplay__seoLink--hidden"
                    key={index}
                    href={url}
                    rel={queryParams.ne || queryParams.es ? 'nofollow' : ''}
                  >
                    {targetName}×{kodawariName?.name} すべての求人を見る
                  </a>
                );
              }
              return <></>;
            });
          }
          if (flag === LABEL_FLAG.LANGUAGE_AND_POSITIONS) {
            return languageAndPositionsUrl.map((url, index) => {
              if (jobCountValueLanguageSkillsAndPositions[index].count === 0) {
                return <></>;
              }
              const kodawariName = opList.find((kodawari) => {
                return kodawari.id === languageAndPositionsOP[index];
              });

              if (initialUrl !== url) {
                return (
                  <a
                    className="initialDisplay__seoLink--hidden"
                    key={index}
                    href={url}
                    rel={queryParams.ne || queryParams.es ? 'nofollow' : ''}
                  >
                    {targetName}×{kodawariName?.name} すべての求人を見る
                  </a>
                );
              }
              return <></>;
            });
          }
          if (flag === LABEL_FLAG.ESTABLISH) {
            return KODAWARI.establish.map((es, index) => {
              const appQueryParamsestablish = createPreBtn(queryParams);
              appQueryParamsestablish.es = [es.value];
              const url = makeStaticUrl(
                appQueryParamsestablish,
                DODA_SITE_URL.jobSearchList
              ).replace(/,/g, '%2C');
              if (rankingList.establish[0][index] === 0) {
                return <></>;
              }
              if (initialUrl !== url) {
                return (
                  <a
                    className="initialDisplay__seoLink--hidden"
                    key={index}
                    href={url}
                    rel={
                      queryParams.ne || queryParams.op || queryParams.ha
                        ? 'nofollow'
                        : ''
                    }
                  >
                    {targetName}×{es?.text} すべての求人を見る
                  </a>
                );
              }
              return <></>;
            });
          }
          if (flag === LABEL_FLAG.INCOME) {
            return UPPER_AND_LOWER_HA.map((ha, index) => {
              const appQueryParamsincome = createPreBtn(queryParams);
              appQueryParamsincome.ha = [ha.lowerId, ha.upperId];
              const url = makeStaticUrl(
                appQueryParamsincome,
                DODA_SITE_URL.jobSearchList
              ).replace(/,/g, '%2C');
              if (rankingList.income[0][index] === 0) {
                return <></>;
              }

              if (initialUrl !== url) {
                return (
                  <a
                    className="initialDisplay__seoLink--hidden"
                    key={index}
                    href={url}
                    rel={
                      !(ha.lowerId === '100' && ha.upperId === '0') ||
                      queryParams.ne ||
                      queryParams.es
                        ? 'nofollow'
                        : ''
                    }
                  >
                    {targetName}×{ha?.name} すべての求人を見る
                  </a>
                );
              }
              return <></>;
            });
          }
          return <></>;
        })()}
      </div>
    </div>
  );
};
